import { forwardRef } from 'react';
import {
    IoArchiveOutline,
    IoChatbubblesOutline,
    IoCheckmark,
    IoChevronDown,
    IoClose,
    IoFileTrayOutline,
    IoFlash,
    IoHandLeftOutline,
    IoNotificationsOutline,
    IoTimeOutline,
    IoWalletOutline,
} from 'react-icons/io5';

import { Anchor, Button, ButtonProps, Group, Loader, Menu, ScrollArea, Text } from '@mantine/core';
import { InboxSort } from 'src/helpers/NotificationPriorityHelpers';

import { Count, NotificationCategory } from 'helpers/NotificationHelpers';

import { useCompanyProfile } from 'hooks/use-company-profile';
import { useCurrentTeam } from 'hooks/use-current-team';
import { useCurrentTeams } from 'hooks/use-current-teams';
import { useInbox, useInboxFilterCounts, useInboxSort } from 'hooks/use-inbox';
import { NotificationService } from 'services/NotificationService';

import cx from './NotificationToolbar.module.scss';

const MENU_MAX_HEIGHT = 320;
const MENU_MAX_WIDTH = 280;
const MENU_STYLES = {
    dropdown: { maxWidth: MENU_MAX_WIDTH },
    itemLabel: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
} as const;

const CATEGORY_OPTIONS = [
    { value: NotificationCategory.MESSAGES, label: 'Messages', icon: <IoChatbubblesOutline /> },
    { value: NotificationCategory.ACCESS, label: 'Access', icon: <IoHandLeftOutline /> },
    { value: NotificationCategory.CREDITS, label: 'Credits', icon: <IoWalletOutline /> },
    { value: NotificationCategory.OTHER, label: 'Other', icon: <IoNotificationsOutline /> },
];

const SORT_OPTIONS = [
    { value: InboxSort.PRIORITY, label: 'Priority', icon: <IoFlash color="var(--mantine-color-yellow-5)" /> },
    { value: InboxSort.LATEST, label: 'Latest', icon: <IoTimeOutline /> },
];

const NotificationToolbar = () => {
    const { unreadOnly, archived } = useInbox();

    return (
        <Group gap={6} wrap="nowrap" align="flex-start">
            <Group gap={6} wrap="wrap" flex={1}>
                <TeamPill />
                <ViewSwitch />
                <CompanyPill />
                <CategoryPills />
                {/* Archiving marks a notification read, so unread and archived never overlap */}
                {!archived && (
                    <Pill
                        active={unreadOnly}
                        onClick={() => NotificationService.setFilters({ unreadOnly: !unreadOnly })}
                    >
                        Unread only
                    </Pill>
                )}
                <ResetFiltersLink />
            </Group>
            <SortPill />
        </Group>
    );
};

const ViewSwitch = () => {
    const { archived } = useInbox();

    return (
        <Button.Group>
            <Pill
                active={!archived}
                leftSection={<IoFileTrayOutline />}
                onClick={() => NotificationService.setFilters({ archived: false })}
            >
                Inbox
            </Pill>
            <Pill
                active={archived}
                leftSection={<IoArchiveOutline />}
                onClick={() => NotificationService.setFilters({ archived: true, unreadOnly: false })}
            >
                Archived
            </Pill>
        </Button.Group>
    );
};

const CategoryPills = () => {
    const { category, archived } = useInbox();
    const { categoryCounts } = useInboxFilterCounts();

    const shownOptions = CATEGORY_OPTIONS.filter(
        (option) => option.value === category || categoryCounts.byCategory[option.value].total > 0,
    );
    // Archived notifications are all read, so their pills count everything instead of what's left to read
    const shownCount = (count: Count) => (archived ? count.total : count.unread);

    return (
        <>
            <Pill
                active={category === null}
                count={shownCount(categoryCounts.all)}
                leftSection={<IoFileTrayOutline />}
                onClick={() => NotificationService.setFilters({ category: null })}
            >
                All
            </Pill>
            {shownOptions.map((option) => (
                <Pill
                    key={option.value}
                    active={option.value === category}
                    count={shownCount(categoryCounts.byCategory[option.value])}
                    leftSection={option.icon}
                    onClick={() => NotificationService.setFilters({ category: option.value })}
                >
                    {option.label}
                </Pill>
            ))}
        </>
    );
};

const SortPill = () => {
    const [sort, setSort] = useInboxSort();
    const selectedOption = SORT_OPTIONS.find((option) => option.value === sort);

    return (
        <Menu position="bottom-end" offset={3} shadow="xl" styles={MENU_STYLES}>
            <Menu.Target>
                <Pill active={false} leftSection={selectedOption?.icon} rightSection={<IoChevronDown size={12} />}>
                    {selectedOption?.label}
                </Pill>
            </Menu.Target>
            <Menu.Dropdown>
                {SORT_OPTIONS.map((option) => (
                    <Menu.Item
                        key={option.value}
                        leftSection={option.icon}
                        onClick={() => setSort(option.value)}
                        rightSection={option.value === sort && <IoCheckmark />}
                    >
                        {option.label}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
};

const TeamPill = () => {
    const { team } = useInbox();
    const teams = useCurrentTeams();
    const currentTeam = useCurrentTeam();

    if (teams.length < 2) {
        return null;
    }

    const selectedTeam = teams.find((option) => option.id === team);
    const orderedTeams = [
        ...teams.filter((option) => option.id === currentTeam?.id),
        ...teams.filter((option) => option.id !== currentTeam?.id),
    ];

    return (
        <Menu position="bottom-start" offset={3} shadow="xl" styles={MENU_STYLES}>
            <Menu.Target>
                <Pill active={Boolean(selectedTeam)} rightSection={<IoChevronDown size={12} />}>
                    {selectedTeam?.name ?? 'All teams'}
                </Pill>
            </Menu.Target>
            <Menu.Dropdown>
                <ScrollArea.Autosize mah={MENU_MAX_HEIGHT} scrollbarSize={8} scrollbars="y">
                    {[{ id: null, name: 'All teams' }, ...orderedTeams].map((option) => {
                        const isCurrent = option.id === currentTeam?.id;

                        return (
                            <Menu.Item
                                key={option.id ?? 'all'}
                                onClick={() => NotificationService.setFilters({ team: option.id })}
                                rightSection={
                                    <Group gap={6} wrap="nowrap">
                                        {isCurrent && (
                                            <Text size="xs" c="dimmed">
                                                Current
                                            </Text>
                                        )}
                                        {option.id === team && <IoCheckmark />}
                                    </Group>
                                }
                            >
                                {option.name}
                            </Menu.Item>
                        );
                    })}
                </ScrollArea.Autosize>
            </Menu.Dropdown>
        </Menu>
    );
};

const CompanyPill = () => {
    const { company } = useInbox();
    const { company: selectedCompany } = useCompanyProfile(company ?? undefined);

    // we only show company filter when company is set from company card
    if (!company) {
        return null;
    }

    return (
        <Pill
            active
            rightSection={<IoClose size={14} />}
            aria-label="Show all companies"
            onClick={() => NotificationService.setFilters({ company: null })}
        >
            {selectedCompany ? selectedCompany?.name : <Loader size={12} color="white" />}
        </Pill>
    );
};

const Pill = forwardRef<
    HTMLButtonElement,
    { active: boolean; count?: number; children: React.ReactNode } & Omit<ButtonProps, 'children'> &
        React.ComponentPropsWithoutRef<'button'>
>(({ active, count, children, className, ...props }, ref) => (
    <Button
        ref={ref}
        size="compact-sm"
        radius="sm"
        variant={active ? 'filled' : 'default'}
        className={[cx.pill, className].filter(Boolean).join(' ')}
        {...props}
    >
        {children}
        {!!count && (
            <Text span inherit fw={400} opacity={0.6} ml={4}>
                {count}
            </Text>
        )}
    </Button>
));

const ResetFiltersLink = () => {
    const { isFiltered } = useInbox();

    return isFiltered ? (
        <Anchor component="button" fz="sm" fw={500} c="black" ml={4} onClick={() => NotificationService.resetFilters()}>
            Reset
        </Anchor>
    ) : null;
};

export { NotificationToolbar };
